<template>
    <component
        :is="href ? 'a' : 'button'"
        ref="buttonRef"
        type="button"
        :class="btnClass"
        :disabled="disabled"
        :href="href"
        :target="target"
        @click="handleClick"
    >
        <span v-if="hasIconBefore" class="pt-button--icon-wrapper">
            <pt-icon v-if="iconBeforeProps" v-bind="iconBeforeProps" />
            <slot name="icon"></slot>
        </span>
        <span v-if="$slots.default" class="pt-button--content">
            <slot></slot>
        </span>
        <span v-if="hasIconAfter" class="pt-button--icon-wrapper">
            <pt-icon v-if="iconAfterProps" v-bind="iconAfterProps" />
            <slot name="iconAfter"></slot>
        </span>
    </component>
</template>

<script lang="ts">
export default {
    name: 'PtButton',
};
</script>
<script setup lang="ts">
import { ref, computed, useSlots } from 'vue';

interface IconConfig {
    name: string;
    color?: string;
    width?: string;
    height?: string;
    size?: string;
}

export interface Props {
    type?: 'text' | 'outline' | 'contain';
    size?: 'large' | 'medium' | 'normal' | 'small';
    color?:
        | 'blue'
        | 'lightblue'
        | 'darkblue'
        | 'green'
        | 'darkgreen'
        | 'red'
        | 'dark'
        | 'purple'
        | 'whiteness'
        | 'grey'
        | 'orange';
    loading?: boolean | 'before' | 'after' | 'cover';
    disabled?: boolean;
    reverse?: boolean;
    fullfilled?: boolean;
    block?: boolean;
    round?: boolean;
    href?: string | boolean;
    target?: HTMLAnchorElement['target'];
    icon?: string | IconConfig;
    iconAfter?: string | IconConfig;
}

const props = withDefaults(defineProps<Props>(), {
    type: 'contain',
    size: 'normal',
    color: 'darkgreen',
});

const emit = defineEmits<{
    (type: 'click', evt: Event): void;
}>();
const slots = useSlots();

const buttonRef = ref<HTMLButtonElement | undefined>(undefined);
const handleClick = (evt: Event) => {
    emit('click', evt);
    if (buttonRef.value) buttonRef.value?.blur();
};

const hasIconBefore = computed(() => Boolean(props.icon || slots.icon));
const hasIconAfter = computed(() => Boolean(props.iconAfter || slots.iconAfter));
const createIconProps = (icon: Props['icon']) =>
    icon
        ? {
              icon: (icon as IconConfig)?.name ?? icon,
              style:
                  typeof icon !== 'object'
                      ? undefined
                      : {
                            ...(icon as IconConfig),
                            ...{
                                width:
                                    (icon as IconConfig)?.size ??
                                    (icon as IconConfig)?.width ??
                                    (icon as IconConfig)?.height ??
                                    '16px',
                                height:
                                    (icon as IconConfig)?.size ??
                                    (icon as IconConfig)?.height ??
                                    (icon as IconConfig)?.width ??
                                    '16px',
                            },
                        },
          }
        : undefined;
const iconBeforeProps = computed(() => createIconProps(props.icon));
const iconAfterProps = computed(() => createIconProps(props.iconAfter));

const btnClass = computed(() => [
    'pt-button',
    `pt-button--${props.type}--${props.color}${props.reverse ? '--reverse' : ''}`,
    `pt-button--${props.size}`,
    {
        'pt-button--block': props.block,
        'pt-button--round': props.round,
        'pt-button--fullfilled': props.fullfilled,
        'is-disabled': Boolean(props.loading) || props.disabled,
        'is-loading-before': props.loading === 'before' || (props.loading === true && !hasIconAfter.value),
        'is-loading-after': props.loading === 'after' || (props.loading === true && hasIconAfter.value),
        'is-loading-cover': props.loading === 'cover',
        'pt-button--before-icon': !!iconBeforeProps.value,
        'pt-button--after-icon': !!iconAfterProps.value,
    },
]);
const disabled = computed(() => props.loading || props.disabled);
const href = computed(() =>
    typeof props.href === 'string' && props.href !== '' && !disabled.value ? props.href : undefined,
);
const target = computed(() => (props.href ? props.target : undefined));
</script>

<style lang="scss" src="../style/button.scss" />
